import { useSelectedCommunity } from '@core/adapters/communityAdapter';
import { useMemo } from 'react';

import * as styles from './HeroBackground.css';

export interface HeroBackgroundProps {
  /**
   * The background image URL to display. If not provided, will use the selected community's hero image or a default.
   */
  imageUrl?: string;
  /**
   * Whether the background should be visible.
   * @default true
   */
  visible?: boolean;
  /**
   * Custom className for additional styling.
   */
  className?: string;
  /**
   * Custom height for the hero background. If not provided, defaults to 600px.
   */
  height?: string;
}

/**
 * HeroBackground Component
 *
 * A reusable background component that displays a hero image with absolute positioning
 * behind the navigation bar and other page content. Supports optional gradient fade
 * to blend into the solid app background color.
 *
 * This component is designed to be used at the page level and renders behind all other content
 * using z-index positioning.
 */
export function HeroBackground({ imageUrl, visible = true, className, height }: HeroBackgroundProps) {
  const { community: selectedCommunity } = useSelectedCommunity();

  // Determine the final image URL to use
  const finalImageUrl = useMemo(() => {
    if (imageUrl) {
      return imageUrl;
    }

    if (selectedCommunity?.superHeroArtUrl) {
      return selectedCommunity.superHeroArtUrl;
    }

    // No fallback image - will use solid color background
    return null;
  }, [imageUrl, selectedCommunity?.superHeroArtUrl]);

  // Create dynamic style for the hero background
  const backgroundStyle = useMemo(() => {
    if (!visible) {
      return {};
    }

    // If we have an image URL, use it
    if (finalImageUrl) {
      return {
        '--hero-background-image': `url(${finalImageUrl})`,
      } as React.CSSProperties;
    }

    // No image - return empty object, CSS will handle solid color fallback
    return {};
  }, [finalImageUrl, visible]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={styles.heroBackgroundWrapper}
      style={{
        opacity: visible ? 0.25 : 0,
        visibility: visible ? 'visible' : 'hidden',
        transition: 'opacity 0.5s ease-out',
        transitionDelay: visible ? '0s' : '0.1s',
        height: height || '600px', // Use custom height or default to 600px
      }}
    >
      <div className={`${styles.heroBackground} ${className || ''}`} style={backgroundStyle} />
    </div>
  );
}
